/* eslint-disable linebreak-style */
import cloneDeep from 'lodash/fp/cloneDeep';
import { addWeekdays } from './FormFunctions';

export class ShoppingCart {
  id = null
  items = []
}

export class ShoppingCartItem {
  allocationCode = null
  permitId = null
  description = null
  amount = 0.00
}

export class Address {
  constructor() {
    this.streetNumber = undefined;
    this.streetDirection = undefined;
    this.streetName = undefined;
    this.streetType = undefined;
    this.suffixDir = undefined;
    this.addressLine2 = undefined;
    this.city = undefined;
    this.state = 'IL';
    this.zipCode = undefined;
    this.unincorporated = false;
  }
}

export class GenericContact {
  constructor() {
    this.address = new Address();
    this.name = undefined;
    this.contact = undefined;
    this.phone = undefined;
    this.email = undefined;
  }
}

export class Facility extends GenericContact {
  constructor() {
    super();
    this.type = null;
    this.ownerAddress = new Address();
    this.permitNumber = undefined;
    this.asbestosPresent = undefined;
    this.builtBefore1982 = true;
    this.houseCount = undefined;
    this.garageCount = undefined;
    this.shedCount = undefined;
    this.contactName = undefined;
    this.contactPhone = undefined;
    this.contactEmail = undefined;
    this.priorUse = undefined;
    this.presentUse = undefined;
    this.futureUse = undefined;
    this.buildingSize = undefined;
    this.buildingFloors = undefined;
    this.constructionYear = undefined;
    this.buildingName = undefined;
  }
}

export class WorkHours {
  constructor() {
    this.workEvenings = null;
    this.workHoursStart = null;
    this.workHoursEnd = null;
    this.workWeekends = null;
  }
}

export class Material {
  constructor() {
    this.id = null
    this.materialType = ''
    this.quantityEstimate = 0.00
    this.unitofMeasure = ''
    this.overrideName = ''
    this.code = ''
    this.linktoRecyclingFacility = ''
    this.facility = null
    this.quantityActual = 0.00
    this.receiptFile = null
    this.fileKey = null
    this.uuid = null;
  }
}

export class Contractor extends GenericContact {
  constructor(contractorType) {
    super();
    this.contractorType = contractorType;
    this.ownerAddress = new Address();
  }
  ownerAddress = null
  contactFirstName = null
  contactLastName = null
  contactPhone = null
  contactEmail = null
  contractorId = null
  fax = null
  contractorIDPH = null
  contractorType = null
  numberofYearsLicensedbytheState = null
  initialRegistrationDate = null
  iDPHLicenseExpirationDate = null
  removalAbatementGlovebag = null
  removalAbatementGrossWetRemoval = null
  removalAbatementHeat = null
  removalAbatementInfrared = null
  removalAbatementOther = null
  removalAbatementSolvent = null
}

export class ContractorApplication {
  constructor(contractor) {
    const cont = cloneDeep(contractor);
    cont.licenseExpirationDate = null;
    cont.numberofYearsLicensedbytheState = null;
    cont.removalAbatementGlovebag = false;
    cont.removalAbatementGrossWetRemoval = false;
    cont.removalAbatementHeat = false;
    cont.removalAbatementInfrared = false;
    cont.removalAbatementOther = false;
    cont.removalAbatementSolvent = false;
    this.contractor = cont;
  }
  id = null
  contractor = null
  violations = []
  files = {
    childSupportAffidavit: null,
    idphContractorLicense: null,
    companyAnnualReport: null,
  }

  submitterName = null
  title = null
  signDate = new Date()
}

export class Violation {
  id = null
  enforcementActionType = null
  currentDisposition = null
  enforcementDate = null
}

export class PermitApplication {
  constructor(projectType) {
    this.projectType = projectType.toLowerCase();
    this.contractor = new Contractor(projectType.toLowerCase());
  }
  exempt = false
  contactEmail = undefined
  confirmEmail = undefined
  startDate = addWeekdays(new Date(), 10).toDate()
  endDate = addWeekdays(new Date(), 11).toDate()
  workHours = new WorkHours()
  asbestosPresent = false
  facility = new Facility()
  disposalSite = new GenericContact()
  transporter = new GenericContact()
  asbestosData = new AsbestosData()
  specialCircumstances = undefined
  revisionCount = undefined
  inspectionFeeAmount = undefined
  variance = {
    safetyRisk: false,
    suddenEvent: false,
    officialOrder: false,
    workStarted: false,

    safetyRiskComment: null,
    suddenEventComment: null,
    officialOrderComment: null,
    workStartedComment: null,

    signature: null,
  }
  name = undefined
  title = undefined
  signDate = new Date()
  files = {
    asbestosAssessmentReport: undefined,
    asbestosInspectionReport: undefined,
    buildingConstructionDate: undefined,
    ccTaxBill: undefined,
    supportingDocumentation: undefined,
    platOfSurvey: undefined,
    taxSummary: undefined,
    correspondence: undefined,
    varianceFinancialBurden: undefined,
    varianceHealthRisk: undefined,
    varianceCourtOrder: undefined,
    supportingDocument: undefined
  }
  existingFileDetails = {
    asbestosAssessmentReport: undefined,
    asbestosInspectionReport: undefined,
    buildingConstructionDate: undefined,
    ccTaxBill: undefined,
    supportingDocumentation: undefined,
    platOfSurvey: undefined,
    taxSummary: undefined,
    correspondence: undefined,
    varianceFinancialBurden: undefined,
    varianceHealthRisk: undefined,
    varianceCourtOrder: undefined,
    supportingDocument: undefined
  }
  projectDesignerId = null
  projectDesignerName = null
  asbestosProjectManagerId = null
  asbestosProjectManagerName = null
  airSamplingProfessionalId = null
  airSamplingProfessionalName = null
  abatementOther = null
  city = null
  addressLine2 = null
  varianceCheckCourtOrder = null
  varianceCheckFinancialBurden = null
  varianceCheckPublicHealth = null
  varianceCheckWorkStartedorComplete = null
  varianceNoRefundAcknowledgement = null
  varianceNotesCourtOrder = null
  varianceNotesFinancialBurden = null
  varianceNotesPublicHealth = null
  varianceNotesWorkStartedorComplete = null
  varianceRequested = null
  descriptionOfWorkPractices = null
  permitIssueDate = null
  omPermit = false
  submittersTitle = null
  partialFullDemo = null
  materials = []
  materialEstimate = null
  finalized = false
}

export class RevisionApplication {
  constructor(permit) {
    if (permit) {
      Object.assign(this, permit);
      this.id = null;
      this.permitId = permit.id;
      this.emailForRevision = permit.contactEmail;
      this.confirmEmail = permit.contactEmail;
      this.workStartDate = permit.startDate;
      this.workEndDate = permit.endDate;
      this.projectOMUpdate = permit.omPermit;
      this.contractorName = this.contractor.name;
      this.omPermit = permit.omPermit;
    }
  }
  changesAbatementInfo = false
  changesContractorInfo = false
  changesDisposalSite = false
  changesEmail = false
  changesPermitDates = false
  changesSiteInfo = false
  changesWasteTransporter = false
  confirmEmail = null
  comments = null
  contractorName = null
  contractor = new Contractor('demolition')
  contractorContactFirstName = null
  contractorContactLastName = null
  contractorContactPhone = null
  contractorContactEmail = null
  facility = new Facility()
  descriptionofACM = null
  descriptionofDemolition = null
  descriptionofLocations = null
  emailForRevision = null
  inspectionFeePaid = null
  notifyApprovalRevSent = null
  permitNumber = null
  projectCancel = null
  projectHold = null
  asbestosData = new AsbestosData()
  revisionComments = null
  revisionFeeAmount = null
  revisionFeePaid = null
  revisionFinishDate = null
  revisionNumber = null
  revisionStartDate = null
  revisionStatus = null
  revisionSubmittalDate = new Date()
  disposalSite = new GenericContact()
  transporter = new GenericContact()
  workHours = new WorkHours()
  revisionSubmitterName = null
  workStartDate = null
  workEndDate = null
  revisionID = null
  permitId = null
  projectType = null
  finalized = false
  files = {}
  projectOMUpdate = false
  omPermit = false
}

export class AsbestosData {
  id = null
  abatementList = null
  abatementGlovebag = null
  abatementGWR = null
  abatementHeat = null
  abatementInfrared = null
  abatementOther = null
  abatementSolvent = null
  asbestosDescriptionProcedureifFound = null
  asbestosProcedureAnalyticalMethod = null
  asbestosRemovalMethods = null
  asbestosRemovalMethodsOther = null
  descriptionofACM = null
  linearftTotalRemoval = null
  locationofACM = null
  pipesNonFriableNoRemovalCat1 = null
  pipesNonFriableNoRemovalCat2 = null
  pipesNonFriableRemovalCat1 = null
  pipesNonFriableRemovalCat2 = null
  pipesRACM = null
  surfaceAreaNonFriableNoRemovalCat1 = null
  surfaceAreaNonFriableNoRemovalCat2 = null
  surfaceAreaNonFriableRemovalCat1 = null
  surfaceAreaNonFriableRemovalCat2 = null
  surfaceAreaRACM = null
  surfaceAreaTotalRemoval = null
  projectManager = null
  inspectorId = null
}

export class OAndMRenewalApplication {
  omFriableSchoolProjectType = false
  omNonFriableSchoolProjectType = false
  omCommercialProjectType = false
  OMProjectTypeList = null
  OMSubmittedList = null

  submitter = {
    contractor: false,
    owner: false,
    projectDesigner: false,
  }
  contactEmail = null
  confirmEmail = null
  facility = new Facility()
  contractor = new Contractor('asbestos')
  permitStartDate = new Date()
  submitterName = null
  submissionDate = new Date()
  disposalSite = new GenericContact()
  transporter = new GenericContact()
  omPermit = true
  submittersTitle = null
  projectDesignerId = null
  projectDesignerName = null
  asbestosProjectManagerId = null
  asbestosProjectManagerName = null
  airSamplingProfessionalId = null
  airSamplingProfessionalName = null
}

export class AirAddEquipmentForm {
  constructor() {
    this.continue = [
      { value: '0', text: 'No' },
      { value: '1', text: 'Yes' },
    ];
  }
  equipmentList = []
  fileList = []
  fileTypes = []
  readyList = []
  fileRequestId = null
  iepaFileUploaded = null
  fileTypeOptions = [{ value: 'Equipment Cut Sheet', text: 'Equipment Cut Sheet' }, { value: 'MSDS', text: 'MSDS' }, { value: 'Process Flow', text: 'Process Flow' }, { value: 'Site Map', text: 'Site Map' }, { value: 'Air Emission Reports', text: 'Air Emission Reports' }]
  currFileType = null
  existingFile = null
  equipmentType = null
  level2Option = null
  usageMetric = null
  quantity = null
  description = null
  scanning = 0
  payerEmail = null
  payerEmailV = null
  iepaPermit = null
  naicsCode = null
  naicsDescription = null
  naicsSector = null
  iepaPermitOptions = [{value: '', text: ''}, {value: 'N/A', text: 'N/A - None'}, {value: 'ROSS', text: 'ROSS'}, {value: 'Life Time', text: 'Life Time'}, {value: 'FESOP', text: 'FESOP'}, {value: 'Title V', text: 'Title V'}]
  totalEmployees = null
  facilitySqFootage = null
  naicsOther = null
}

export class SiteRequest {
  constructor() {
    this.ownerAddress = new Address();
    this.owner = null;
    this.contactFirstName = null;
    this.contactLastName = null;
    this.contactPhone = null;
    this.contactEmail = null;
    this.contactTitle = null;
    this.confirmEmail = null;
  }
}

export class SiteAuthentication {
  constructor() {
    this.siteNumber = null;
  }
}

export class OpenBurnPermitApplication {
  constructor() { }

  // Verification
  isMunicipality = null
  hasPermit = null
  permitNumber = null

  // Applicant Information
  applicantFirstName = null
  applicantLastName = null
  applicantAddress = new Address()
  applicantPhone = null
  contactFirstName = null
  contactLastName = null
  contactPhone = null
  contactTitle = null
  applicantEmail = null
  confirmEmail = null
  typeOfRequest = null
  typeOfRequestOtherDesc = null
  donatedBuilding = null

  // Site Information
  siteName = null
  siteAddress = null
  siteAddress2 = null;
  siteCity = null;
  siteState = null;
  siteZip = null;
  crossStreets = null
  dateOfBurn = new Date()
  totalHours = null
  moreThanDay = false
  totalSessions = null
  otherDates = null
  onSiteContact = null
  onSitePhone = null

  //Burn Materials
  //json object: {name, amount/size, description}
  burnMaterials = [{ name: null, amount: null, description: null, objectId: null }]
  methodOfExtinguishment = null

  individualsNotifiedInd = null
  individualsNotifiedExplaination = null
  isBurn100FeetAwayInd = null

  // Authorization
  signature = null
  signatureTitle = null
  signatureDate = new Date()
  additionalComments = null

  // File
  files = {
    statePermit: undefined,
    asbestosAssessmentFile: undefined,
    demolitionPermit: undefined,
    writtenInstructions: undefined,
    ffWrittenPlan: undefined,
    vicinitySketch: undefined,
    printedMap: undefined,

  }
  existingFileDetails = {
    statePermit: undefined,
    asbestosAssessmentFile: undefined,
    demolitionPermit: undefined,
    writtenInstructions: undefined,
    ffWrittenPlan: undefined,
    vicinitySketch: undefined,
    printedMap: undefined,

  }
}

export class OpenBurnRecPermitApplication {
  constructor() { }

  address1 = null
  address2 = null
  applicationDate = null
  permitId = null
  burnAddress1 = null
  burnAddress2 = null
  burnCity = null
  burnEndTime = null
  burnStartTime = null
  burnState = null
  burnZip = null
  city = null
  companyName = null
  contactEmail = null
  confirmEmail = null
  contactFirstName = null
  contactLastName = null
  contactPhone = null
  dateOfBurn = null
  localFireDepartment = null
  nameOfForestPreserve = null
  signature = null
  signatureDate = new Date()
  signatureTitle = null
  state = null
  zip = null
}


export class OpenBurnFormData {
  constructor() {
    this.typeOfRequestOptions = [{ value: 'Firefighting Instruction / Research', text: 'Firefighting Instruction / Research' }, { value: 'Disaster Waste', text: 'Disaster Waste' }, { value: 'Landscape Waste w/ Air Curtain Destructor', text: 'Landscape Waste w/ Air Curtain Destructor' }, { value: 'Prairie or Ecology Management', text: 'Prairie or Ecology Management' }, { value: 'Other', text: 'Other' }];
    this.yesno = [
      { value: null, text: '' },
      { value: false, text: 'No' },
      { value: true, text: 'Yes' },
    ];
    this.states = [
      { value: 'AL', text: 'Alabama' },
      { value: 'AK', text: 'Alaska' },
      { value: 'AZ', text: 'Arizona' },
      { value: 'AR', text: 'Arkansas' },
      { value: 'CA', text: 'California' },
      { value: 'CO', text: 'Colorado' },
      { value: 'CT', text: 'Connecticut' },
      { value: 'DE', text: 'Delaware' },
      { value: 'DC', text: 'District Of Columbia' },
      { value: 'FL', text: 'Florida' },
      { value: 'GA', text: 'Georgia' },
      { value: 'HI', text: 'Hawaii' },
      { value: 'ID', text: 'Idaho' },
      { value: 'IL', text: 'Illinois' },
      { value: 'IN', text: 'Indiana' },
      { value: 'IA', text: 'Iowa' },
      { value: 'KS', text: 'Kansas' },
      { value: 'KY', text: 'Kentucky' },
      { value: 'LA', text: 'Louisiana' },
      { value: 'ME', text: 'Maine' },
      { value: 'MD', text: 'Maryland' },
      { value: 'MA', text: 'Massachusetts' },
      { value: 'MI', text: 'Michigan' },
      { value: 'MN', text: 'Minnesota' },
      { value: 'MS', text: 'Mississippi' },
      { value: 'MO', text: 'Missouri' },
      { value: 'MT', text: 'Montana' },
      { value: 'NE', text: 'Nebraska' },
      { value: 'NV', text: 'Nevada' },
      { value: 'NH', text: 'New Hampshire' },
      { value: 'NJ', text: 'New Jersey' },
      { value: 'NM', text: 'New Mexico' },
      { value: 'NY', text: 'New York' },
      { value: 'NC', text: 'North Carolina' },
      { value: 'ND', text: 'North Dakota' },
      { value: 'OH', text: 'Ohio' },
      { value: 'OK', text: 'Oklahoma' },
      { value: 'OR', text: 'Oregon' },
      { value: 'PA', text: 'Pennsylvania' },
      { value: 'RI', text: 'Rhode Island' },
      { value: 'SC', text: 'South Carolina' },
      { value: 'SD', text: 'South Dakota' },
      { value: 'TN', text: 'Tennessee' },
      { value: 'TX', text: 'Texas' },
      { value: 'UT', text: 'Utah' },
      { value: 'VT', text: 'Vermont' },
      { value: 'VA', text: 'Virginia' },
      { value: 'WA', text: 'Washington' },
      { value: 'WV', text: 'West Virginia' },
      { value: 'WI', text: 'Wisconsin' },
      { value: 'WY', text: 'Wyoming' },
    ];
  }
}
